// PACKAGES
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectionStrategy, ElementRef, NgZone, ViewChild } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
// APP
import { InvData } from 'src/app/dashboard/file-explorer-page/file-explorer-page.types';
import { formatBytes } from 'src/app/_utils/utils';
import { Project } from 'src/types/Project';
import { ActiveProjectState } from 'src/app/_state/projects/projects.reducer';
import { FileExplorerStateService, SortDirection, SortMethod, ViewMode } from 'src/app/_state/file-explorer/file-explorer-state.service';

// virtual scrolling needs fixed sizes; these match the stylesheet
const ROW_HEIGHT = 32;
const TILE_WIDTH = 124;
const TILE_ROW_HEIGHT = 120;

@Component({
    selector: 'app-explorer-contents',
    templateUrl: './explorer-contents.component.html',
    styleUrls: ['./explorer-contents.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExplorerContentsComponent implements OnInit, OnDestroy {
  @Input() loading: boolean;
  @Input() noData: boolean;
  
  @Input() activeDirectory: InvData;
  @Output() activeDirectoryChange = new EventEmitter();
  @Input() checkedContent: InvData[];
  @Output() checkedContentChange = new EventEmitter();

  selectedDataDetails: InvData['details'];
  /** the checked content, for a quick look-up per row */
  checked = new Set<InvData>();

  currentSortingMethod: SortMethod;
  currentSortingDirection: SortDirection;
  viewMode: ViewMode;

  readonly rowHeight = ROW_HEIGHT;
  readonly tileRowHeight = TILE_ROW_HEIGHT;
  tilesPerRow = 1;
  private tileRowsCache: { contents: InvData[], tilesPerRow: number, rows: InvData[][] };
  private gridResizeObserver: ResizeObserver;

  showFileMenu: boolean = false;
  fileMenuLocation: { x: number, y: number };

  activeProject: Project;
  
  _fileExplorerStateListener: Subscription;
  _activeProjectStateListener: Subscription;

  constructor(
    private fileExplorerStateService: FileExplorerStateService,
    private store: Store<{ activeProject: ActiveProjectState }>,
    private ngZone: NgZone,
  ) { }

  @ViewChild('viewport') listViewport?: CdkVirtualScrollViewport;
  @ViewChild('gridViewport') gridScroller?: CdkVirtualScrollViewport;

  /** fit as many tiles in a grid row as the grid's width allows, whenever it resizes */
  @ViewChild('gridViewport', { read: ElementRef }) set gridViewport(viewport: ElementRef<HTMLElement> | undefined) {
    this.gridResizeObserver?.disconnect();
    if(!viewport) {
      return;
    }

    this.gridResizeObserver = new ResizeObserver(([entry]) => this.ngZone.run(() => {
      this.tilesPerRow = Math.max(1, Math.floor(entry.contentRect.width / TILE_WIDTH));
    }));
    this.gridResizeObserver.observe(viewport.nativeElement);
  }

  ngOnInit() {
    // listen to file explorer state changes in ngrx store
    this._fileExplorerStateListener = this.fileExplorerStateService.explorerState.subscribe(
      ({ selectedContent, activeDirectory, sortMethod, sortDirection, viewMode, infoPanelContent }) => {
        // a new folder or sort starts at the top, not where the last list was scrolled to
        if(this.activeDirectory && (activeDirectory !== this.activeDirectory
          || sortMethod !== this.currentSortingMethod || sortDirection !== this.currentSortingDirection)) {
          this.listViewport?.scrollToIndex(0);
          this.gridScroller?.scrollToIndex(0);
        }

        this.viewMode = viewMode
        this.currentSortingMethod = sortMethod;
        this.currentSortingDirection = sortDirection;
        this.selectedDataDetails = infoPanelContent;

        this.activeDirectory = activeDirectory;
        this.checkedContent = selectedContent;
        this.checked = new Set(selectedContent);
      }
    );

    // listen to active project state changes in ngrx store
    this._activeProjectStateListener = this.store.select('activeProject').subscribe(({ activeProject }) => {
      this.activeProject = activeProject;
    })
  }

  ngOnDestroy() {
    this._fileExplorerStateListener.unsubscribe();
    this._activeProjectStateListener.unsubscribe();
    this.gridResizeObserver?.disconnect();
  }

  /** the loaded contents in rows of tiles for the grid, worked out again only when the contents or the width change */
  get tileRows(): InvData[][] {
    const contents = this.activeDirectory.contents;
    if(this.tileRowsCache?.contents !== contents || this.tileRowsCache.tilesPerRow !== this.tilesPerRow) {
      const rows: InvData[][] = [];
      for(let i = 0; i < contents.length; i += this.tilesPerRow) {
        rows.push(contents.slice(i, i + this.tilesPerRow));
      }
      this.tileRowsCache = { contents, tilesPerRow: this.tilesPerRow, rows };
    }
    return this.tileRowsCache.rows;
  }

  /** load the next page once the user scrolls within about 2 viewports of the last loaded row */
  onScrolledIndexChange(firstVisibleRow: number, visibleRows: number, loadedRows: number) {
    if(firstVisibleRow + visibleRows * 3 >= loadedRows) {
      this.loadMore();
    }
  }

  /** load the next page, or the first page again after it failed */
  loadMore() {
    this.fileExplorerStateService.loadPage(this.activeDirectory, true);
  }

  /** View content details in the Info Panel side viewer */
  viewContentDetails(content: InvData) {
    this.selectedDataDetails = content.details;
    this.fileExplorerStateService.setInfoPanelContent(this.selectedDataDetails);
  }

  /** select content for actions */
  checkContent(content: InvData, multiselect: boolean) {
    if(multiselect) {
      if(this.checkedContent.includes(content)) {
        this.checkedContent = this.checkedContent.filter(data => data.details.id !== content.details.id);
      } else {
        this.checkedContent = [ ...this.checkedContent, content ];
      }
    } else {
      this.checkedContent = [ content ];
    }

    this.fileExplorerStateService.setSelectedContent(this.checkedContent);
  }

  /** clear all selected content */
  clearCheckedContent() {
    this.fileExplorerStateService.setSelectedContent([]);
  }

  /** set current active directory to specified folder */
  setActiveDirectory(folder: InvData) {
    this.fileExplorerStateService.setActiveDirectory(folder);
    this.fileExplorerStateService.toggleFolderExpansion(folder.details.id);
  }

  /** Change sorting method and store in redux store */
  changeSortingMethod(method: SortMethod) {
    // if not changing methods, change direction of sort
    if(this.currentSortingMethod === method) {
      this.fileExplorerStateService.setSort(method, this.currentSortingDirection === 'ASC' ? 'DESC' : 'ASC');
    }
    // if changing sort methods
    else {
      this.fileExplorerStateService.setSort(method, 'ASC');
    }
  }

  /** if all loaded contents are selected, unselect all, otherwise select everything loaded */
  checkAll() {
    if(this.checkedContent.length === this.activeDirectory.contents.length) {
      this.clearCheckedContent();
      return;
    } else {
      this.fileExplorerStateService.setSelectedContent([ ...this.activeDirectory.contents ]);
      return;
    }
  }

  /** format number into size string */
  formatBytes(size: number) {
    return formatBytes(size, 2);
  }

  /** convert a date in seconds to milliseconds */
  secondsToMilliseconds(date: number | undefined) {
    return date * 1000 || undefined;
  }

}
